import {
  Badge,
  Button,
  Code,
  Drawer,
  Group,
  ScrollArea,
  Stack,
  Text
} from '@mantine/core'
import { H1, SortableTable } from '@packages/ui'
import { useSupabase } from '@packages/supabase'
import {
  IconCircleChevronLeft,
  IconRefresh,
  IconZoomIn
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

interface LogRow {
  id: string
  tenant_id: string
  tenant_name: string | null
  requested_by_email: string | null
  requested_source: 'admin' | 'self'
  deletion_reason: string | null
  status: string
  verification: string | null
  execution_time_ms: number | null
  created_at: string
  completed_at: string | null
  pre_check: unknown
  post_check: unknown
  steps: unknown
  bookkeeping_archive: unknown
  errors: unknown
}

const statusColor = (s: string) =>
  s === 'completed'
    ? 'green'
    : s === 'partial'
      ? 'yellow'
      : s === 'failed'
        ? 'red'
        : s === 'cancelled'
          ? 'gray'
          : 'blue'

export const DeletionLogs = () => {
  const { supabase } = useSupabase()
  const navigate = useNavigate()
  const [rows, setRows] = useState<LogRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<LogRow | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error } = await supabase
      .from('tenant_deletion_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200)
    setLoading(false)
    if (error) {
      setError(error.message)
      return
    }
    setRows((data as LogRow[]) ?? [])
  }, [supabase])

  useEffect(() => {
    fetch()
  }, [fetch])

  const drawerJson = useMemo(() => {
    if (!selected) return ''
    return JSON.stringify(selected, null, 2)
  }, [selected])

  return (
    <Stack c="white" gap="md">
      <Group justify="space-between">
        <H1 className="text-white">
          <Button
            variant="transparent"
            c="white"
            leftSection={<IconCircleChevronLeft />}
            onClick={() => navigate('/tenants')}
            p={0}
          >
            Back
          </Button>{' '}
          Deletion Logs
        </H1>
        <Button
          variant="transparent"
          c="white"
          leftSection={<IconRefresh size={16} />}
          loading={loading}
          onClick={fetch}
        >
          Refresh
        </Button>
      </Group>

      {error && <Text c="red">{error}</Text>}

      <SortableTable
        data={rows}
        pageSize={25}
        columns={[
          {
            key: 'created_at',
            label: 'When',
            render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm')
          },
          { key: 'tenant_name', label: 'Tenant' },
          {
            key: 'requested_source',
            label: 'Source',
            render: (v: string) => (
              <Badge color={v === 'admin' ? 'blue' : 'teal'} variant="light">
                {v}
              </Badge>
            )
          },
          { key: 'requested_by_email', label: 'Actor' },
          {
            key: 'status',
            label: 'Status',
            render: (v: string) => (
              <Badge color={statusColor(v)} variant="filled">
                {v}
              </Badge>
            )
          },
          {
            key: 'verification',
            label: 'Verify',
            render: (v: string | null) =>
              v ? (
                <Badge
                  color={v === 'clean' ? 'green' : 'red'}
                  variant="light"
                >
                  {v}
                </Badge>
              ) : (
                '-'
              )
          },
          {
            key: 'execution_time_ms',
            label: 'Elapsed',
            render: (v: number | null) => (v ? `${v}ms` : '-')
          },
          {
            key: 'id',
            label: '',
            render: (_v, row: LogRow) => (
              <Button
                size="xs"
                variant="default"
                leftSection={<IconZoomIn size={14} />}
                onClick={() => setSelected(row)}
              >
                JSON
              </Button>
            )
          }
        ]}
      />

      <Drawer
        opened={!!selected}
        onClose={() => setSelected(null)}
        title={`Deletion log: ${selected?.tenant_name ?? selected?.tenant_id ?? ''}`}
        position="right"
        size="xl"
      >
        <ScrollArea h="80vh" type="auto">
          <Code block fz="11px">
            {drawerJson}
          </Code>
        </ScrollArea>
      </Drawer>
    </Stack>
  )
}

export default DeletionLogs
